import type GroupAddress from "../models/GroupAdress";
import { pdfHeaderText } from "./pdfTagName";
import type BaseModel from "../models/BaseModel";
import type Signal from "../models/Signal";
import type Channel from "../models/Channel";

export type GroupAddressUsage = {
  ancestorTag: string;
  channelDescription: string;
  channelLabel?: string;
  channelName?: string;
  ancestorName: string;
  signalNumber: string;
};
export type GroupAddressReportRow = {
  level: number;
  address: string;
  name: string;
  usages: number;
  usageDetails?: GroupAddressUsage[];
};
export type GroupAddressReport = {
  detailedUsage: boolean;
  sections: Array<{ title: string; rows: GroupAddressReportRow[] }>;
  totals: { mainGroups: number; middleGroups: number; addresses: number; usages: number };
};

/** Sorts KNX addresses numerically, so 1/2/9 precedes 1/2/10. */
function sortedAddresses(addresses: Iterable<GroupAddress>): GroupAddress[] {
  return Array.from(addresses).sort((left, right) =>
    left.fullAddress.localeCompare(right.fullAddress, "de", { numeric: true }),
  );
}

/** Labels channels using their UI name and connected names; other signal owners retain their display name. */
function usageOwnerName(parent: BaseModel | undefined): string {
  const channel = parent as
    | (BaseModel & Partial<Pick<Channel, "getChannelNameForUI" | "nameOrConnectedNames">>)
    | undefined;
  if (typeof channel?.getChannelNameForUI === "function") {
    return `${channel.getChannelNameForUI()}:\u00a0${channel.nameOrConnectedNames ?? ""}`;
  }
  return parent?.nameOfDefaultName || "";
}

/** Mirrors the navigator's ordering and labels while excluding defining consumer references. */
function collectUsageDetails(signals: Signal[], splitUsageColumns = false): GroupAddressUsage[] {
  const sorted = signals.filter((signal) => !(signal.parent as BaseModel | undefined)?.isConsumer);
  sorted.sort((left, right) => {
    if (left.className !== right.className) return left.className.localeCompare(right.className);
    return left.fullTag.localeCompare(right.fullTag);
  });
  return sorted.map((signal) => {
    const parent = signal.parent as BaseModel | undefined;
    const channel = parent as (BaseModel & Partial<Pick<Channel, "getChannelNameForUI" | "nameOrConnectedNames">>) | undefined;
    return {
      ancestorTag: parent?.parent?.fullTag || "",
      channelDescription: usageOwnerName(parent),
      ...(splitUsageColumns ? {
        channelLabel: typeof channel?.getChannelNameForUI === "function" ? channel.getChannelNameForUI() : parent?.fullTag || "",
        channelName: typeof channel?.getChannelNameForUI === "function" ? channel.nameOrConnectedNames ?? "" : parent?.nameOfDefaultName || "",
      } : {}),
      ancestorName: (parent?.parent as BaseModel | undefined)?.name || "",
      signalNumber: signal.number ?? "",
    };
  });
}

/** Preserves the existing counts and optionally adds signal details without consumer references. */
export function collectGroupAddressReport(
  addresses: Iterable<GroupAddress>,
  detailedUsage = false,
  splitUsageColumns = false,
): GroupAddressReport {
  const report: GroupAddressReport = {
    detailedUsage,
    sections: [],
    totals: { mainGroups: 0, middleGroups: 0, addresses: 0, usages: 0 },
  };
  for (const main of sortedAddresses(addresses)) {
    report.totals.mainGroups += 1;
    const rows: GroupAddressReportRow[] = [];
    for (const middle of sortedAddresses(main.children)) {
      report.totals.middleGroups += 1;
      const middleRow = { level: 2, address: middle.fullAddress, name: middle.name, usages: 0 };
      rows.push(middleRow);
      for (const address of sortedAddresses(middle.children)) {
        let usages = 0;
        const signals = address.getSignalBackpointers(false);
        for (const signal of signals) {
          const parent = signal?.parent as BaseModel | undefined;
          if (parent && !parent.isConsumer && !parent.isMultiplexer) usages += 1;
        }
        rows.push({
          level: 3,
          address: address.fullAddress,
          name: address.name,
          usages,
          ...(detailedUsage ? { usageDetails: collectUsageDetails(signals, splitUsageColumns) } : {}),
        });
        middleRow.usages += usages;
        report.totals.addresses += 1;
        report.totals.usages += usages;
      }
    }
    report.sections.push({ title: pdfHeaderText(main), rows });
  }
  return report;
}
